import React from 'react';
import { Calculator, Percent, Landmark, BarChart3, Lock } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { authGate } from '../../lib/authGate';
import { UserProfile } from '../../types';

interface Tool {
  id: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  path: string;
  requiresInvestorAccount?: boolean;
}

interface ToolsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
  isGuest?: boolean;
  userProfile?: UserProfile | null;
}

const tools: Tool[] = [
  {
    id: 'calculator',
    label: 'ROI Calculator',
    description: 'Capital appreciation & exit strategy',
    icon: Calculator,
    path: '/tools/calculator',
    requiresInvestorAccount: true
  },
  {
    id: 'rental-yield',
    label: 'Rental Yield',
    description: 'Gross & net yield calculations',
    icon: Percent,
    path: '/tools/rental-yield',
    requiresInvestorAccount: false
  },
  {
    id: 'mortgage',
    label: 'Mortgage Estimator',
    description: 'UAE mortgage calculations',
    icon: Landmark,
    path: '/tools/mortgage',
    requiresInvestorAccount: true
  },
  {
    id: 'analytics',
    label: 'Market Analysis',
    description: 'Market trends & insights',
    icon: BarChart3,
    path: '/analytics',
    requiresInvestorAccount: true
  }
];

const ToolsSheet: React.FC<ToolsSheetProps> = ({ isOpen, onClose, onNavigate, isGuest = false, userProfile }) => {
  const location = useLocation();
  
  // Check if user has investor account
  const hasInvestorAccount = userProfile?.buyerType === 'Investor';
  
  if (!isOpen) return null;

  const handleToolSelect = (tool: Tool) => {
    onClose();
    
    // Rental yield is open to all logged-in users, no gating needed
    if (tool.path === '/tools/rental-yield') {
      onNavigate(tool.path);
      return;
    }
    
    // For guests, show sign-in modal
    if (isGuest) {
      const toolName = tool.id;
      authGate.openGate(toolName, {
        path: location.pathname,
        action: `access-${toolName}`,
        targetPath: tool.path
      });
      return;
    }
    
    // For logged-in users without investor account, show investor account modal
    if (!hasInvestorAccount && tool.requiresInvestorAccount) {
      window.dispatchEvent(new CustomEvent('openInvestorAccountModal', {
        detail: { path: tool.path }
      }));
      return;
    }
    
    // User has access, navigate normally
    onNavigate(tool.path);
  };

  return (
    <div className="fixed inset-0 z-[100] md:hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]"
        onClick={onClose}
      />
      
      {/* Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl animate-[slideUp_0.3s_ease-out] max-h-[70vh] flex flex-col">
        {/* Handle */}
        <div className="pt-3 pb-2 flex justify-center">
          <div className="w-12 h-1.5 bg-slate-300 rounded-full" />
        </div>
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 pb-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">Tools</h2>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-2">
            {tools.map((tool) => {
              const Icon = tool.icon;
              const isDisabled = !isGuest && !hasInvestorAccount && tool.requiresInvestorAccount;
              return (
                <button
                  key={tool.id}
                  onClick={() => handleToolSelect(tool)}
                  className={`w-full text-left px-4 py-4 rounded-xl flex items-center gap-4 group transition-colors ${
                    isDisabled
                      ? 'bg-slate-50 opacity-60 hover:bg-slate-100'
                      : 'bg-slate-50 hover:bg-slate-100'
                  }`}
                >
                  <div className={`size-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0 transition-colors ${
                    isDisabled
                      ? 'text-slate-400'
                      : 'text-slate-600 group-hover:bg-slate-900 group-hover:text-white group-hover:border-slate-900'
                  }`}>
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold group-hover:text-slate-900 ${
                      isDisabled ? 'text-slate-400' : 'text-slate-900'
                    }`}>{tool.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{tool.description}</div>
                  </div>
                  {isDisabled && (
                    <Lock size={16} className="text-amber-500 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolsSheet;
